import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "../../lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md";

// Label-only by design: no icon + label pairs (see CLAUDE.md). Icon-only
// actions use IconButton instead.
interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
}

const baseRing =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas";

const variants: Record<Variant, string> = {
  primary:
    "bg-accent text-text-on-accent hover:bg-accent-hover active:bg-accent-active",
  secondary:
    "bg-bg-surface text-text-primary border border-border-default hover:bg-bg-inset",
  ghost:
    "text-text-secondary hover:text-text-primary hover:bg-bg-inset",
  danger:
    "bg-status-error-soft text-status-error border border-border-subtle hover:bg-status-error hover:text-text-on-accent",
};

const sizes: Record<Size, string> = {
  sm: "h-7 px-2.5 text-[13px] gap-1.5 rounded-md",
  md: "h-9 px-3.5 text-[14px] gap-2 rounded-md",
};

export const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      variant = "primary",
      size = "md",
      className,
      children,
      type = "button",
      ...rest
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          "inline-flex items-center justify-center font-medium",
          "transition-colors duration-fast ease-out select-none",
          "disabled:opacity-40 disabled:pointer-events-none",
          baseRing,
          sizes[size],
          variants[variant],
          className,
        )}
        {...rest}
      >
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";
